import React from 'react';

import { Sorters } from '@lumapps/lumx-filter-and-sort/components/Sorters';
import { SORT_FIELDS } from '@lumapps/lumx-filter-and-sort/constants';
import { SortersType } from '@lumapps/lumx-filter-and-sort/types';
import { usePrevious } from '@lumapps/lumx-hooks/hooks/usePrevious';
import { Alignment, FlexBox, ButtonProps } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { PickerDialog, PickerDialogProps } from '../PickerDialog';

export interface SearchParams {
    query?: string;
    sortOrder?: string;
}

export interface PagePickerDialogProps<T>
    extends Omit<PickerDialogProps<T>, 'afterList' | 'onScrollEnd' | 'searchValue' | 'onSearchChange'> {
    /** Whether the list is loading more elements. */
    isListLoadingMore: boolean;
    /** Skeleton to display while loading. */
    listSkeleton: React.ReactNode;
    /** available sorts */
    sorts?: SortersType[];
    /** Fetch page with params. */
    fetchPage?(params: SearchParams): void;
    /** Fetch more for infinite scroll. */
    fetchMore(): void;
    /** whether it should prevent the closing of the dialog */
    shouldPreventClose?: boolean;
    /** props to be passed into the confirm button */
    confirmButtonProps?: ButtonProps;
    /** whether to fetch on the component mount or not */
    fetchOnMount?: boolean;
    /** callback on search */
    onSearch?: (query: string) => void;
    /** callback on sort change */
    onSortChange?: (sort: SortersType) => void;
}

/**
 * A picker dialog fetching pages using a query string and an order.
 *
 * @family Dialog
 * @param PagePickerDialogProps
 * @returns PagePickerDialog
 */
export const PagePickerDialog = <T,>(props: PagePickerDialogProps<T>) => {
    const {
        isOpen,
        isListLoading,
        isListLoadingMore,
        listSkeleton,
        fetchPage,
        fetchMore,
        items,
        sorts = SORT_FIELDS,
        shouldPreventClose = true,
        confirmButtonProps,
        additionalFilterFields,
        fetchOnMount = true,
        initSelectedItems,
        onSearch,
        onSortChange,
        ...forwardedProps
    } = props;
    const { translateKey } = useTranslate();
    const [sortField, setSortField] = React.useState<SortersType>(sorts[0]);
    const [query, setQuery] = React.useState('');
    const isLoadingMoreRef = usePrevious(isListLoadingMore);

    const onQueryChange = (qry: string) => {
        if (onSearch && isOpen) {
            onSearch(qry);
        }

        setQuery(qry);
    };

    const onSortFieldChange = (sort: SortersType) => {
        if (onSortChange) {
            onSortChange(sort);
        }

        setSortField(sort);
    };

    // Search on open or search value change or sort order change.
    React.useEffect(() => {
        if (isOpen && fetchOnMount && fetchPage) {
            fetchPage({ query, sortOrder: sortField?.id });
        }
    }, [fetchPage, isOpen, sortField, query, fetchOnMount]);

    React.useEffect(() => {
        if (sorts && sorts.length > 0) {
            setSortField(sorts[0]);
        }
    }, [sorts]);

    return (
        <PickerDialog<T>
            isOpen={isOpen}
            onSearchChange={onQueryChange}
            additionalFilterFields={
                <>
                    {additionalFilterFields}
                    {sorts && sorts.length > 0 && (
                        <FlexBox marginAuto={Alignment.left}>
                            <Sorters
                                isChangingOrderEnabled={false}
                                choices={sorts}
                                sort={sortField?.id}
                                onChange={(sort) => onSortFieldChange(sorts.find((s) => s.id === sort) as SortersType)}
                                sortButtonProps={{
                                    label: translateKey(GLOBAL.SORTING),
                                }}
                            />
                        </FlexBox>
                    )}
                </>
            }
            onScrollEnd={fetchMore}
            isListLoading={isListLoading}
            items={isListLoading ? [] : items}
            afterList={(isListLoading || isListLoadingMore) && listSkeleton}
            confirmButtonProps={confirmButtonProps || { children: translateKey(GLOBAL.ADD) }}
            // reset items only if the last operation was not a load more
            resetListOnItemsChange={!isLoadingMoreRef}
            shouldPreventClose={shouldPreventClose}
            initSelectedItems={initSelectedItems}
            searchFieldProps={{
                debounced: true,
                ...(forwardedProps.searchFieldProps || {}),
            }}
            {...forwardedProps}
        />
    );
};
